import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaUsers,
  FaUserTie,
  FaUserPlus,
  FaUserCheck,
  FaUserShield,
  FaFlag,
  FaYoutube,
  FaFacebookSquare,
  FaInstagram,
  FaSyncAlt,
  FaTrophy,
  FaEye,
  FaExternalLinkAlt,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaLink,
  FaThumbtack,
  FaLayerGroup,
  FaWallet,
  FaMoneyBillWave,
  FaMobileAlt,
  FaHandHoldingUsd,
  FaCommentDots,
  FaChevronRight,
  FaCheckCircle,
  FaBell,
} from "react-icons/fa";
import axios from "axios";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles & animation ================= */

const ANIM_CSS = `
@keyframes ycnpFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
.ycnp-in { animation: ycnpFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.ycnp-press { transition: transform .15s ease, box-shadow .2s ease, background-color .2s ease, border-color .2s ease; }
.ycnp-press:hover { transform: translateY(-2px); box-shadow: 0 10px 24px -12px rgba(15,23,42,.25); }
.ycnp-press:active { transform: translateY(0) scale(.97); box-shadow: none; }
.ycnp-row { transition: background-color .15s ease, transform .1s ease; }
.ycnp-row:active { transform: scale(.985); }
.ycnp-row:hover .ycnp-chev { transform: translateX(3px); color: #0891b2; }
.ycnp-chev { transition: transform .2s ease, color .2s ease; }
@keyframes ycnpPulse { 0% { box-shadow: 0 0 0 0 rgba(245,158,11,.55); } 70% { box-shadow: 0 0 0 7px rgba(245,158,11,0); } 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); } }
.ycnp-dot { animation: ycnpPulse 1.8s ease-out infinite; }
@keyframes ycnpBar { from { width: 0; } }
.ycnp-bar { animation: ycnpBar .9s cubic-bezier(.2,.7,.2,1) both; }
@media (prefers-reduced-motion: reduce) {
  .ycnp-in, .ycnp-dot, .ycnp-bar { animation: none; }
  .ycnp-press, .ycnp-press:hover, .ycnp-press:active, .ycnp-row:active { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";

/* ================= helpers ================= */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

// Pull an array out of any response shape
const toArray = (d, keys = []) => {
  if (Array.isArray(d)) return d;
  if (!d || typeof d !== "object") return [];
  for (const k of keys) if (Array.isArray(d[k])) return d[k];
  const firstArr = Object.values(d).find((v) => Array.isArray(v));
  return firstArr || [];
};

const statusOf = (w) =>
  String(w?.status ?? w?.withdraw_status ?? w?.request_status ?? "")
    .toLowerCase()
    .trim();

const platformOf = (type = "") => {
  const t = String(type).toLowerCase();
  if (t.includes("youtube")) return "youtube";
  if (t.includes("facebook")) return "facebook";
  if (t.includes("instagram")) return "instagram";
  return "other";
};

const PLATFORM = {
  youtube: { icon: FaYoutube, text: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-900/30", bar: "bg-red-500" },
  facebook: { icon: FaFacebookSquare, text: "text-blue-600 dark:text-blue-400", bg: "bg-blue-50 dark:bg-blue-900/30", bar: "bg-blue-600" },
  instagram: { icon: FaInstagram, text: "text-pink-600 dark:text-pink-400", bg: "bg-pink-50 dark:bg-pink-900/30", bar: "bg-pink-500" },
  other: { icon: FaLink, text: "text-slate-500", bg: "bg-slate-100 dark:bg-slate-700", bar: "bg-slate-400" },
};

/* ================= count-up number ================= */

const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (typeof target !== "number" || Number.isNaN(target)) return undefined;
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setVal(target);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const CountUp = ({ value }) => {
  const v = useCountUp(Number(value || 0));
  return <>{fmt(v)}</>;
};

// undefined = loading, null = not available, number = show
const Val = ({ v, w = "w-8" }) => {
  if (v === undefined)
    return <span className={`inline-block h-4 ${w} rounded bg-slate-200 dark:bg-slate-600 animate-pulse align-middle`} />;
  if (v === null) return <span className="text-slate-400">–</span>;
  return <CountUp value={v} />;
};

const TypeLabel = ({ type }) => {
  const P = PLATFORM[platformOf(type)];
  const Icon = P.icon;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs ${P.bg} ${P.text}`}>
        <Icon />
      </span>
      <span className="text-slate-700 dark:text-slate-200">{type || "-"}</span>
    </span>
  );
};

/* ================= page ================= */

const Dash = () => {
  const [dashboard, setDashboard] = useState({
    total_users: 0,
    total_admin: 0,
    admin_req: 0,
    total_report: 0,
    youtube_videos: 0,
    youtube_shorts: 0,
    facebook_videos: 0,
    facebook_reel: 0,
    instagram_reel: 0,
  });
  const [dashState, setDashState] = useState("loading"); // loading | ok | error
  const [topCoin, setTopCoin] = useState([]);
  const [topView, setTopViews] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("karma");
  const [sort, setSort] = useState({ field: null, dir: "desc" });

  const { baseUrl } = useBaseUrl();
  const { setCurrentColor, setCurrentMode, currentMode } = useStateContext();

  /* ----- API helpers ----- */

  const post = useCallback(
    (file, action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      return axios
        .post(`${baseUrl}/${file}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        })
        .then((r) => r.data);
    },
    [baseUrl]
  );

  const get = useCallback(
    (path) => axios.get(`${baseUrl}/${path}`, { withCredentials: true }).then((r) => r.data),
    [baseUrl]
  );

  /* ----- main dashboard data ----- */

  const fetchDashboard = useCallback(async () => {
    setDashState("loading");
    setErrorMessage("");
    try {
      const apiData = await post("users.php", "dashboard");
      if (apiData && !apiData.error) {
        setDashboard({
          total_users: Number(apiData.total_users ?? 0),
          total_admin: Number(apiData.total_admin ?? 0),
          admin_req: Number(apiData.admin_req ?? 0),
          total_report: Number(apiData.total_report ?? 0),
          youtube_videos: Number(apiData.youtube_videos ?? 0),
          youtube_shorts: Number(apiData.youtube_shorts ?? 0),
          facebook_videos: Number(apiData.facebook_videos ?? 0),
          facebook_reel: Number(apiData.facebook_reel ?? 0),
          instagram_reel: Number(apiData.instagram_reel ?? 0),
        });
        setTopCoin(Array.isArray(apiData.top_coin) ? apiData.top_coin : []);
        setTopViews(Array.isArray(apiData.top_views) ? apiData.top_views : []);
        setDashState("ok");
      } else {
        setDashState("error");
        setErrorMessage(apiData?.error || "Could not load dashboard data.");
      }
    } catch (error) {
      setDashState("error");
      setErrorMessage(
        "Server not reachable: " + (error.response?.data?.error || error.message)
      );
    }
  }, [post]);

  /* ----- every sidebar section ----- */

  const fetchStats = useCallback(() => {
    const jobs = {
      adsImages: () => get("ads_images.php?action=list").then((d) => ({ count: toArray(d, ["images", "data"]).length })),
      ownerPosts: () => get("admin_post.php?action=list").then((d) => ({ count: toArray(d, ["posts", "list", "data"]).length })),
      ownerLinks: () => post("videos.php", "owner-video-list").then((d) => ({ count: toArray(d, ["videos", "data", "list"]).length })),
      userMsgReports: () => post("group_report.php", "getUserMessageReports").then((d) => ({ count: toArray(d, ["reports"]).length })),
      groupMsgReports: () => post("group_report.php", "getGroupMessageReports").then((d) => ({ count: toArray(d, ["reports"]).length })),
      groups: () =>
        post("group.php", "groupListWithMembers").then((d) => {
          const g = toArray(d, ["groups"]);
          return {
            count: g.length,
            yt: g.filter((x) => String(x.platform).toLowerCase() === "youtube").length,
            fb: g.filter((x) => String(x.platform).toLowerCase() === "facebook").length,
          };
        }),
      joinReq: () => post("group.php", "groupJoiningRequestList").then((d) => ({ count: toArray(d, ["requests"]).length })),
      groupMembers: () => post("group.php", "getGroupMembers").then((d) => ({ count: toArray(d, ["members"]).length })),
      groupPackages: () => post("group.php", "getPackageList").then((d) => ({ count: toArray(d, ["data"]).length })),
      wallets: () => post("wallet.php", "walletList").then((d) => ({ count: toArray(d, ["wallets"]).length })),
      withdrawals: () =>
        post("wallet.php", "withdrawRequestList").then((d) => {
          const list = toArray(d, ["withdrawals"]);
          const pendingList = list.filter((w) => /pend|process|request|^0$/.test(statusOf(w)));
          return {
            count: list.length,
            pending: pendingList.length,
            accepted: list.filter((w) => /accept|approv|success|paid|complet|^1$/.test(statusOf(w))).length,
            cancelled: list.filter((w) => /cancel|reject|declin|fail|^2$/.test(statusOf(w))).length,
            pendingAmount: pendingList.reduce((s, w) => s + Number(w.amount ?? w.withdraw_amount ?? 0), 0),
          };
        }),
      adsWallets: () => post("wallet.php", "adsWalletList").then((d) => ({ count: toArray(d, ["wallets"]).length })),
      customers: () => post("customer.php", "customerList").then((d) => ({ count: toArray(d, ["customers", "data", "list"]).length })),
      custReq: () => post("customer.php", "customerRequestList").then((d) => ({ count: toArray(d, ["requests"]).length })),
      custPackages: () => post("customer.php", "packageList").then((d) => ({ count: toArray(d, ["data"]).length })),
      appUpdates: () => post("app_update.php", "appUpdateList").then((d) => ({ count: toArray(d, ["updates"]).length })),
      subAdmins: () => get("subadmin_api.php").then((d) => ({ count: toArray(d, ["data", "users", "subadmins"]).length })),
      // Same lists the pages show, so the numbers always match what opens
      adminReqList: () => post("users.php", "admin-request").then((d) => ({ count: toArray(d, ["data", "requests", "users"]).length })),
      pendingReports: () => post("users.php", "reported-users", { reported: "0" }).then((d) => ({ count: toArray(d, ["reports", "data"]).length })),
    };

    setStats({});
    Object.entries(jobs).forEach(([key, job]) => {
      job()
        .then((res) => setStats((s) => ({ ...s, [key]: res })))
        .catch(() => setStats((s) => ({ ...s, [key]: null })));
    });
  }, [get, post]);

  const refreshAll = useCallback(() => {
    fetchDashboard();
    fetchStats();
    setLastUpdated(new Date());
  }, [fetchDashboard, fetchStats]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");
    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ----- value getters ----- */

  const dash = (k) => (dashState === "loading" ? undefined : dashState === "error" ? null : dashboard[k]);
  const st = (k, field = "count") => {
    const v = stats[k];
    if (v === undefined) return undefined;
    if (v === null) return null;
    return v[field];
  };

  const isLoading = dashState === "loading";

  /* ----- sections ----- */

  const attention = [
    { label: "Admin requests", value: st("adminReqList"), to: "/admin-req", icon: FaUserPlus },
    {
      label: "Withdrawal requests",
      value: st("withdrawals", "pending"),
      to: "/wallet/withdrawal-req",
      icon: FaMoneyBillWave,
      sub: st("withdrawals", "pendingAmount") ? `₹${fmt(st("withdrawals", "pendingAmount"))} waiting` : null,
    },
    { label: "Group joining requests", value: st("joinReq"), to: "/group/join-request", icon: FaUserPlus },
    { label: "Customer requests", value: st("custReq"), to: "/customer/request", icon: FaUserCheck },
    { label: "Reports", value: st("pendingReports"), to: "/report", icon: FaFlag },
    { label: "User message reports", value: st("userMsgReports"), to: "/user/message-reports", icon: FaCommentDots },
    { label: "Group message reports", value: st("groupMsgReports"), to: "/group/message-reports", icon: FaCommentDots },
  ];

  const pendingTotal = attention.reduce((s, a) => s + (typeof a.value === "number" ? a.value : 0), 0);

  const kpis = [
    { label: "Total Users", value: dash("total_users"), to: "/users/all", icon: FaUsers, accent: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300" },
    { label: "Total Admins", value: dash("total_admin"), to: "/users/admin", icon: FaUserTie, accent: "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300" },
    {
      label: "Groups",
      value: st("groups"),
      to: "/group/list",
      icon: FaLayerGroup,
      accent: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300",
      sub: stats.groups ? `YouTube ${stats.groups.yt}, Facebook ${stats.groups.fb}` : null,
    },
    { label: "Customers", value: st("customers"), to: "/customer/list", icon: FaUserCheck, accent: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300" },
  ];

  const contentRows = [
    { label: "Youtube Videos", value: dashboard.youtube_videos, platform: "youtube", to: "/videos/youtube-video" },
    { label: "Youtube Shorts", value: dashboard.youtube_shorts, platform: "youtube", to: "/videos/youtube-short" },
    { label: "Facebook Videos", value: dashboard.facebook_videos, platform: "facebook", to: "/videos/facebook-video" },
    { label: "Facebook Reel", value: dashboard.facebook_reel, platform: "facebook", to: "/videos/facebook-reel" },
    { label: "Instagram Reel", value: dashboard.instagram_reel, platform: "instagram", to: "/videos/instagram-reel" },
  ];
  const totalContent = contentRows.reduce((s, r) => s + Number(r.value || 0), 0);

  // Same sections and pages as the sidebar
  const modules = [
    {
      title: "General",
      icon: FaThumbtack,
      tint: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200",
      items: [
        { label: "Report Request", to: "/report", value: st("pendingReports"), alert: true },
        { label: "App Ads", to: "/app-ads", value: st("adsImages") },
        { label: "Owner Post", to: "/owner-post", value: st("ownerPosts") },
      ],
    },
    {
      title: "App Users",
      icon: FaUsers,
      tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
      items: [
        { label: "All Users", to: "/users/all", value: dash("total_users") },
        { label: "Creator List", to: "/users/creator" },
        { label: "Admin List", to: "/users/admin", value: dash("total_admin") },
        { label: "Admin Request", to: "/admin-req", value: st("adminReqList"), alert: true },
        { label: "User Message Report", to: "/user/message-reports", value: st("userMsgReports"), alert: true },
      ],
    },
    {
      title: "Videos",
      icon: FaYoutube,
      tint: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300",
      items: [
        { label: "YouTube", to: "/youtube", value: dashState === "ok" ? dashboard.youtube_videos + dashboard.youtube_shorts : dash("youtube_videos") },
        { label: "Youtube Videos", to: "/videos/youtube-video", value: dash("youtube_videos") },
        { label: "Youtube Shorts", to: "/videos/youtube-short", value: dash("youtube_shorts") },
        { label: "Instagram Reels", to: "/videos/instagram-reel", value: dash("instagram_reel") },
        { label: "Facebook Video", to: "/videos/facebook-video", value: dash("facebook_videos") },
        { label: "Facebook Reels", to: "/videos/facebook-reel", value: dash("facebook_reel") },
      ],
    },
    {
      title: "Groups",
      icon: FaLayerGroup,
      tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300",
      items: [
        { label: "List Of Group", to: "/group/list", value: st("groups") },
        { label: "Joining Request", to: "/group/join-request", value: st("joinReq"), alert: true },
        { label: "Group Member Wallet", to: "/group/wallet", value: st("wallets") },
        { label: "Package Configuration", to: "/group-packages", value: st("groupPackages") },
        { label: "All Group Members List", to: "/group/all-members", value: st("groupMembers") },
        { label: "Group Message Report", to: "/group/message-reports", value: st("groupMsgReports"), alert: true },
      ],
    },
    {
      title: "Withdrawals",
      icon: FaMoneyBillWave,
      tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300",
      items: [
        { label: "Withdrawal Request", to: "/wallet/withdrawal-req", value: st("withdrawals", "pending"), alert: true },
        { label: "Accepted Withdrawal", to: "/wallet/withdrawal", value: st("withdrawals", "accepted") },
        { label: "Cancelled Withdrawal", to: "/wallet/withdrawal-cancel", value: st("withdrawals", "cancelled") },
      ],
    },
    {
      title: "Customer",
      icon: FaUserCheck,
      tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300",
      items: [
        { label: "List Customer", to: "/customer/list", value: st("customers") },
        { label: "Customer Request", to: "/customer/request", value: st("custReq"), alert: true },
        { label: "Packages", to: "/customer-packages", value: st("custPackages") },
      ],
    },
    {
      title: "Monetization",
      icon: FaHandHoldingUsd,
      tint: "bg-lime-50 text-lime-700 dark:bg-lime-900/30 dark:text-lime-300",
      items: [
        { label: "Active Youtube Link", to: "/customer/link/active/youtube" },
        { label: "Active Facebook Link", to: "/customer/link/active/facebook" },
      ],
    },
    {
      title: "Wallets & Links",
      icon: FaWallet,
      tint: "bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300",
      items: [
        { label: "Ads Reward Wallet", to: "/ads-reward-wallet", value: st("adsWallets") },
        { label: "Owner Links", to: "/owner-link", value: st("ownerLinks") },
      ],
    },
    {
      title: "System",
      icon: FaUserShield,
      tint: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300",
      items: [
        { label: "App Updates", to: "/app-updates", value: st("appUpdates"), icon: FaMobileAlt },
        { label: "System User", to: "/system/user", value: st("subAdmins") },
      ],
    },
  ];

  /* ----- table ----- */

  const baseRows = tab === "karma" ? topCoin : topView;

  const tableRows = useMemo(() => {
    if (!sort.field) return baseRows;
    return [...baseRows].sort((a, b) => {
      let x = a[sort.field];
      let y = b[sort.field];
      const nx = Number(x);
      const ny = Number(y);
      if (x !== "" && y !== "" && !Number.isNaN(nx) && !Number.isNaN(ny)) {
        x = nx;
        y = ny;
      }
      if (x < y) return sort.dir === "asc" ? -1 : 1;
      if (x > y) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [baseRows, sort]);

  const toggleSort = (field) =>
    setSort((s) => ({ field, dir: s.field === field && s.dir === "desc" ? "asc" : "desc" }));

  const SortTh = ({ field, children, right }) => {
    const active = sort.field === field;
    const Icon = !active ? FaSort : sort.dir === "asc" ? FaSortUp : FaSortDown;
    return (
      <th className={`px-4 py-3 font-medium whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
        <button
          type="button"
          onClick={() => toggleSort(field)}
          className="inline-flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-white"
        >
          {children}
          <Icon size={11} className={active ? "text-cyan-600" : "text-slate-300 dark:text-slate-500"} />
        </button>
      </th>
    );
  };

  const leaders = [
    { title: "Most karma", item: topCoin[0], value: topCoin[0]?.point_used, unit: "points used", icon: FaTrophy },
    { title: "Most views", item: topView[0], value: topView[0]?.views_num, unit: "views", icon: FaEye },
  ];

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const delay = (i) => ({ animationDelay: `${i * 50}ms` });

  /* ================= render ================= */

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen"
    >
      <style>{ANIM_CSS}</style>

      {/* Header */}
      <div className="ycnp-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Dashboard</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Overview</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{today}</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Updated at {lastUpdated.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button
            type="button"
            onClick={refreshAll}
            disabled={isLoading}
            className="ycnp-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2"
          >
            <FaSyncAlt className={isLoading ? "animate-spin" : ""} size={12} />
            Refresh
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="ycnp-in mb-6 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm flex items-center justify-between gap-3">
          <span>{errorMessage}</span>
          <button type="button" onClick={refreshAll} className="font-semibold underline">
            Try again
          </button>
        </div>
      )}

      {/* Needs attention */}
      <div className={`${CARD} ycnp-in p-5`} style={delay(1)}>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300">
              <FaBell />
            </span>
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">Needs attention</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Requests and reports waiting for action</p>
            </div>
          </div>
          <span
            className={`text-sm font-semibold px-3 py-1 rounded-full ${
              pendingTotal > 0
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
            }`}
          >
            {pendingTotal > 0 ? `${fmt(pendingTotal)} pending` : "All clear"}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {attention.map((a, i) => {
            const Icon = a.icon;
            const hot = typeof a.value === "number" && a.value > 0;
            return (
              <Link
                key={a.label}
                to={a.to}
                style={delay(i + 2)}
                className={`ycnp-in ycnp-press group rounded-lg border p-3.5 flex items-center gap-3 ${
                  hot
                    ? "border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/20"
                    : "border-slate-200 dark:border-slate-700"
                }`}
              >
                <span
                  className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${
                    hot
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                      : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300"
                  }`}
                >
                  <Icon />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-600 dark:text-slate-300 truncate">{a.label}</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white tabular-nums leading-tight">
                    <Val v={a.value} />
                  </p>
                  {a.sub && <p className="text-xs text-amber-700 dark:text-amber-300 truncate">{a.sub}</p>}
                </div>
                {hot ? (
                  <span className="ycnp-dot w-2.5 h-2.5 rounded-full bg-amber-500" />
                ) : typeof a.value === "number" ? (
                  <FaCheckCircle className="text-emerald-500" />
                ) : null}
              </Link>
            );
          })}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <Link
              key={k.label}
              to={k.to}
              style={delay(i + 4)}
              className={`${CARD} ycnp-in ycnp-press p-5 flex flex-col`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{k.label}</span>
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.accent}`}>
                  <Icon />
                </span>
              </div>
              <span className="mt-3 text-3xl font-bold tracking-tight text-slate-900 dark:text-white tabular-nums">
                <Val v={k.value} w="w-16" />
              </span>
              <span className="mt-1 text-xs text-slate-500 dark:text-slate-400 min-h-[16px]">{k.sub || ""}</span>
            </Link>
          );
        })}
      </div>

      {/* Content + leading posts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className={`${CARD} ycnp-in p-5 lg:col-span-2`} style={delay(8)}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">Content</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Posts by platform</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                <Val v={dashState === "ok" ? totalContent : dash("youtube_videos")} w="w-12" />
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Total posts</p>
            </div>
          </div>
          <div className="space-y-1">
            {contentRows.map((r) => {
              const pct = totalContent ? Math.round((Number(r.value || 0) * 100) / totalContent) : 0;
              const P = PLATFORM[r.platform];
              const Icon = P.icon;
              return (
                <Link
                  key={r.label}
                  to={r.to}
                  className="ycnp-row block rounded-lg -mx-2 px-2 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/40"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${P.bg} ${P.text}`}>
                      <Icon />
                    </span>
                    <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-200">{r.label}</span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums">
                      <Val v={dashState === "ok" ? r.value : dashState === "loading" ? undefined : null} />
                    </span>
                    <span className="w-12 text-right text-xs text-slate-500 dark:text-slate-400 tabular-nums">{pct}%</span>
                    <FaChevronRight size={10} className="ycnp-chev text-slate-300" />
                  </div>
                  <div className="mt-2 ml-11 mr-5 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                    {dashState === "ok" && (
                      <div className={`ycnp-bar h-full rounded-full ${P.bar}`} style={{ width: `${pct}%` }} />
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className={`${CARD} ycnp-in p-5`} style={delay(9)}>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">Leading posts</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Top post right now</p>
          <div className="mt-4 space-y-3">
            {leaders.map((l) => {
              const Icon = l.icon;
              return (
                <div key={l.title} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                    <Icon className="text-cyan-600" /> {l.title}
                  </div>
                  {isLoading ? (
                    <div className="h-12 mt-3 rounded-md bg-slate-100 dark:bg-slate-700 animate-pulse" />
                  ) : l.item ? (
                    <>
                      <div className="mt-2 flex items-baseline gap-1.5">
                        <span className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                          <CountUp value={l.value} />
                        </span>
                        <span className="text-sm text-slate-500 dark:text-slate-400">{l.unit}</span>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2 text-sm">
                        <TypeLabel type={l.item.link_type} />
                        <a
                          href={l.item.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ycnp-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 font-medium whitespace-nowrap"
                        >
                          Open <FaExternalLinkAlt size={10} />
                        </a>
                      </div>
                    </>
                  ) : (
                    <p className="mt-2 text-sm text-slate-500">No posts yet</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* All sections (same as sidebar) */}
      <div className="flex items-end justify-between mt-8 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">All sections</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Every sidebar page with its live count. Click any row to open it.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
        {modules.map((m, mi) => {
          const MIcon = m.icon;
          return (
            <div key={m.title} className={`${CARD} ycnp-in p-4`} style={delay(mi + 10)}>
              <div className="flex items-center gap-3 px-1 mb-2">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${m.tint}`}>
                  <MIcon />
                </span>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{m.title}</h3>
              </div>
              <ul>
                {m.items.map((it) => {
                  const hot = it.alert && typeof it.value === "number" && it.value > 0;
                  const hasValue = "value" in it;
                  return (
                    <li key={it.label}>
                      <Link
                        to={it.to}
                        className="ycnp-row flex items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/40"
                      >
                        <span className="flex-1 text-sm text-slate-700 dark:text-slate-200">{it.label}</span>
                        {hot && <span className="ycnp-dot w-2 h-2 rounded-full bg-amber-500" />}
                        {hasValue ? (
                          <span
                            className={`text-sm font-semibold tabular-nums ${
                              hot ? "text-amber-700 dark:text-amber-300" : "text-slate-900 dark:text-white"
                            }`}
                          >
                            <Val v={it.value} />
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-cyan-700 dark:text-cyan-400">Open</span>
                        )}
                        <FaChevronRight size={10} className="ycnp-chev text-slate-300" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Top 5 table */}
      <div className={`${CARD} ycnp-in mt-8`} style={delay(20)}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-5">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">
              {tab === "karma" ? "Top 5 Videos With Highest Karma" : "Top 5 Videos With Highest Views"}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Click a column name to sort</p>
          </div>
          <div className="inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
            {[
              { id: "karma", label: "Highest Karma", icon: FaTrophy },
              { id: "views", label: "Highest Views", icon: FaEye },
            ].map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setTab(t.id);
                    setSort({ field: null, dir: "desc" });
                  }}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 active:scale-95 ${
                    active
                      ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm"
                      : "text-slate-500 dark:text-slate-300 hover:text-slate-800"
                  }`}
                >
                  <Icon size={12} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-y border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 font-medium text-left w-12">#</th>
                <SortTh field="post_id">Post ID</SortTh>
                <SortTh field="user_id">User ID</SortTh>
                <SortTh field="link_type">Type</SortTh>
                <th className="px-4 py-3 font-medium text-left">Link</th>
                <SortTh field="point_used" right>Points Used</SortTh>
                <SortTh field="views_num" right>Views</SortTh>
                <SortTh field="link_date">Date</SortTh>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody key={tab} className="divide-y divide-slate-100 dark:divide-slate-700">
              {isLoading &&
                [0, 1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    <td colSpan={9} className="px-4 py-3">
                      <div className="h-6 rounded-md bg-slate-100 dark:bg-slate-700 animate-pulse" />
                    </td>
                  </tr>
                ))}

              {!isLoading && tableRows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-slate-500">
                    No videos to show yet
                  </td>
                </tr>
              )}

              {!isLoading &&
                tableRows.map((row, i) => {
                  const rank = baseRows.indexOf(row) + 1;
                  const d = parseDate(row.link_date);
                  return (
                    <tr
                      key={`${row.post_id}-${rank}`}
                      style={delay(i)}
                      className="ycnp-in hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex w-6 h-6 items-center justify-center rounded-md text-xs font-semibold ${
                            rank <= 3
                              ? "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300"
                              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                          }`}
                        >
                          {rank}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap tabular-nums text-slate-700 dark:text-slate-200">{row.post_id}</td>
                      <td className="px-4 py-3 whitespace-nowrap tabular-nums">
                        <Link
                          to={`/user-profile/${row.user_id}`}
                          className="text-cyan-700 dark:text-cyan-400 hover:underline"
                          title="Open user profile"
                        >
                          {row.user_id}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <TypeLabel type={row.link_type} />
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={row.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={row.link_url}
                          className="block max-w-[220px] truncate text-slate-600 dark:text-slate-300 hover:text-cyan-700 hover:underline"
                        >
                          {row.link_url}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900 dark:text-white">{fmt(row.point_used)}</td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900 dark:text-white">{fmt(row.views_num)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {d ? (
                          <>
                            <div className="text-slate-700 dark:text-slate-200">
                              {d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </>
                        ) : (
                          row.link_date || "-"
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => window.open(row.link_url, "_blank")}
                          className="ycnp-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                          <FaExternalLinkAlt size={10} /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dash;